if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = { 
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        // NOTE: previously this was `if (!this.elements.thumbnails) return;`
        // placed BEFORE the slideChanged listener below. On layouts without
        // thumbnails (e.g. "stacked", used on mobile) that meant slideChanged
        // was never bound at all, so swiping to a video slide never triggered
        // playback. The listener now binds unconditionally; only the
        // thumbnail-specific wiring below still requires thumbnails to exist.
        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));

        if (!this.elements.thumbnails) return;

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        console.log('[MG] onSlideChanged fired', event.detail.currentElement);
        if (this.elements.thumbnails) {
          const thumbnail = this.elements.thumbnails.querySelector(
            `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
          );
          this.setActiveThumbnail(thumbnail);
        }
        // Previously this method only updated the active thumbnail and never
        // touched playback, which is why swiping to a video slide left it
        // sitting on the poster/play button. Now it also triggers the same
        // load+play path that a thumbnail click uses.
        this.playActiveMedia(event.detail.currentElement);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (!deferredMedia) return;

        deferredMedia.loadContent(false);

        // Autoplay on all viewport sizes now, not just mobile.
        this.forcePlayMobile(deferredMedia);
      }

    // Waits for the video/iframe element to exist AND be ready to play
      // (loadedmetadata/canplay), then forces muted + playsinline + autoplay.
      // Calling .play() before the element has buffered anything gets
      // silently rejected on real mobile browsers (readyState 0 issue) —
      // that's the actual reason autoplay was failing.
      forcePlayMobile(deferredMedia, attempts = 0) {
        if (attempts > 30) return;

        const mediaEl = deferredMedia.querySelector('video, iframe');
        if (!mediaEl) {
          requestAnimationFrame(() => this.forcePlayMobile(deferredMedia, attempts + 1));
          return;
        }

        if (mediaEl.tagName === 'VIDEO') {
          mediaEl.muted = true;
          mediaEl.defaultMuted = true;
          mediaEl.playsInline = true;
          mediaEl.setAttribute('muted', '');
          mediaEl.setAttribute('playsinline', '');
          mediaEl.setAttribute('autoplay', '');

          // Guard: don't re-enter if we're already trying to play this
          // exact element — this is what was causing the AbortError.
          if (mediaEl.dataset.mgPlayPending === 'true') return;

          const tryPlay = () => {
            mediaEl.dataset.mgPlayPending = '';
            const p = mediaEl.play();
            if (p !== undefined) {
              p.catch((err) => {
                console.log('[MG] video.play() REJECTED:', err.name, err.message);
              });
            }
          }; 

          if (mediaEl.readyState >= 2) {
            tryPlay();
          } else {
            mediaEl.dataset.mgPlayPending = 'true';
            // NOTE: no explicit .load() — the element already started
            // loading when it was appended to the DOM. Forcing another
            // load() here is what aborted the in-flight play() before.
            mediaEl.addEventListener('canplay', tryPlay, { once: true });
          }
        } else if (mediaEl.tagName === 'IFRAME') {
          try {
            const src = new URL(mediaEl.src);
            src.searchParams.set('autoplay', '1');
            src.searchParams.set('mute', '1');
            const newSrc = src.toString();
            if (mediaEl.src !== newSrc) mediaEl.src = newSrc;
          } catch (e) {}
        }
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
